import { Refresh } from "@mui/icons-material";
import { IconButton, InputAdornment, Tooltip } from "@mui/material";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch, useTranslation } from "hooks";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SiteModel } from "types";
import { canEditSiteRecord, getSiteLabel } from "utils";
import SelectInput from "./SelectInput";
import TextInput from "./TextInput";

interface Data {
  sites?: SiteModel[];
  totalRecords?: number;
}
interface ISiteSelect {
  value?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  adminOnly?: boolean;
  /** Site ids to omit from the dropdown (e.g. already break-glass unlocked). */
  excludeIds?: string[];
  /** When true, do not auto-pick the first site if value is empty. */
  disableAutoSelect?: boolean;
  /** Placeholder when disableAutoSelect and no value (e.g. super-admin unlock flow). */
  emptyLabel?: string;
  startAdornment?: React.ReactNode;
  onChange: (siteId: string) => void;
  onSites?: (sites: SiteModel[]) => void;
}

export default function SiteSelect({
  value,
  hideLabel,
  disabled,
  adminOnly,
  excludeIds,
  disableAutoSelect,
  emptyLabel,
  startAdornment,
  onChange,
  onSites
}: ISiteSelect) {
  const [{ userData }] = useContextController();
  const [reload, setReload] = useState(false);

  const t = useTranslation();

  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");

  const sites = useMemo(() => {
    let data = response?.data?.sites || [];
    if (adminOnly && userData) {
      data = data.filter((site) => canEditSiteRecord(userData, site._id));
    }
    const blocked = new Set((excludeIds || []).map(String));
    if (blocked.size) {
      data = data.filter((site) => !blocked.has(String(site._id)));
    }
    if (value && data.some((elm) => elm._id === value)) {
      return data;
    }
    // Keep an explicit selection visible (forms), unless it is excluded on purpose.
    if (value && !blocked.has(String(value))) {
      return [...data, { _id: value } as SiteModel];
    }
    return data;
  }, [response, value, adminOnly, userData, excludeIds]);

  useEffect(() => {
    if (!sites.length) return;
    onSites?.(sites);
  }, [onSites, sites]);

  const siteLabel = useMemo(() => {
    const val = sites.find((item) => item._id === value);
    if (val) {
      return getSiteLabel(val);
    }
    return "No Site Selected.";
  }, [sites, value]);

  const { enqueueSnackbar } = useSnackbar();

  const onReload = useCallback(() => setReload((prevState) => !prevState), []);

  useEffect(() => {
    if (!error) return;
    enqueueSnackbar(error?.message || JSON.stringify(error), {
      variant: "error"
    });
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    searchParams.append("limit", "0");
    const params = `?${searchParams.toString()}`;
    callFetchGet(ENDPOINTS.sites + params).catch(console.error);
  }, [callFetchGet, reload]);

  useEffect(() => {
    if (disableAutoSelect) return;
    if (!sites.length) return;
    if (value && sites.some((site) => site._id === value)) return;
    onChange(sites[0]._id);
  }, [sites, value, onChange, disableAutoSelect]);

  if (disabled) {
    return <TextInput disabled fullWidth label="Site" value={siteLabel} />;
  }

  if (sites.length) {
    return (
      <SelectInput
        id="site-select"
        label={!hideLabel ? t["Site"] : undefined}
        helperText={hideLabel ? "" : undefined}
        startAdornment={startAdornment}
        displayEmpty={disableAutoSelect && !value}
        data={
          disableAutoSelect && !value
            ? [
                {
                  label: emptyLabel || t["Select a site"] || "Select a site",
                  value: ""
                },
                ...sites.map((site) => ({
                  label: getSiteLabel(site),
                  value: site._id
                }))
              ]
            : sites.map((site) => ({
                label: getSiteLabel(site),
                value: site._id
              }))
        }
        value={value || ""}
        onChange={({ target: { value: next } }) => {
          if (!next) return;
          onChange(next as string);
        }}
        loading={isLoading}
        fullWidth
      />
    );
  }

  return (
    <TextInput
      disabled
      fullWidth
      label={!hideLabel ? t["Site"] : undefined}
      helperText={hideLabel ? "" : undefined}
      value={isLoading ? "Loading..." : "No Sites Found"}
      onClick={onReload}
      endAdornment={
        <InputAdornment position="end">
          <Tooltip title="Retry">
            <IconButton onClick={onReload}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      }
    />
  );
}
